'use client'

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { AuthModal } from '@/components/auth/AuthModal'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthSignupCard } from '@/components/auth/AuthSignupCard'
import { useBranding } from '@/hooks/useBranding'
import { VideoEmbed } from '@/components/media/VideoEmbed'
import { useImageManager } from '@/components/image/ImageManagerProvider'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Globe2,
  ImagePlus,
  LockKeyhole,
  Maximize2,
  Pencil,
  Play,
  Plus,
  Trash2,
  Users,
  Video,
  X,
} from 'lucide-react'
import {
  AnimatePresence,
  motion,
} from 'framer-motion'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { RichTextEditor } from '@/components/funnel/RichTextEditor'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { CommunityAccess } from '@/lib/communityAccess.server'
import { resolveAuthNextFromStartPayload } from '@/lib/navigation/resolveAuthNext'
import { API_CONFIG } from '@/config/api'
import { useSearchParams } from 'next/navigation'

/* -------------------------------------------------------------------------- */
/*                                   TYPES                                    */
/* -------------------------------------------------------------------------- */

type AboutModule = {
  module_key: string
  enabled: boolean
  sort_order: number
  settings: Record<string, any>
}

type StartPayload = {
  sellerId: number
  home: any
  isOwner: boolean
  isBrandless: boolean
  hasWhitelabel: boolean
  companystatus: number
}

type AuthView =
  | 'login'
  | 'signup'

type MediaItem =
  | {
      id: string
      type: 'image'
      src: string
    }
  | {
      id: string
      type: 'video'
      url: string
    }

type HighlightItem = {
  id: string
  image?: string
  title: string
  description?: string
  url?: string
  cta?: string
  enabled?: boolean
}

type PublicSpace = {
  id?: number | string
  space_id?: number | string
  category_id?: number | string
  name?: string
  title?: string
  description?: string
  image?: string
  image_url?: string
  icon?: string
  public?: boolean
}

type PublicMember = {
  id?: number | string
  customer_id?: number | string
  name?: string
  firstname?: string
  lastname?: string
  avatar?: string
  image?: string
}

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

async function ajaxPost(
  url: string,
  data: any
) {
  // @ts-ignore
  if (
    typeof window !==
      'undefined' &&
    (window as any).$
      ?.ajax
  ) {
    return await new Promise(
      (
        resolve,
        reject
      ) => {
        // @ts-ignore
        ;(window as any).$.ajax(
          {
            url,
            type: 'POST',
            contentType:
              'application/json; charset=utf-8',
            data: JSON.stringify(
              data
            ),
            dataType: 'json',
            success:
              resolve,
            error: () =>
              reject(
                new Error(
                  'ajax_failed'
                )
              ),
          }
        )
      }
    )
  }

  await new Promise<void>(
    (
      resolve,
      reject
    ) => {
      const xhr =
        new XMLHttpRequest()

      xhr.open(
        'POST',
        url,
        true
      )

      xhr.setRequestHeader(
        'Content-Type',
        'application/json; charset=utf-8'
      )

      xhr.onreadystatechange =
        () => {
          if (
            xhr.readyState !==
            4
          )
            return

          if (
            xhr.status >=
              200 &&
            xhr.status <
              300
          ) {
            resolve()
            return
          }

          reject(
            new Error(
              `HTTP_${xhr.status}`
            )
          )
        }

      xhr.send(
        JSON.stringify(
          data
        )
      )
    }
  )
}

function resolveBrandImage(
  src?: string | null,
  siteOrigin?: string
) {
  if (!src) return ''

  const clean =
    src.trim()

  if (
    /^https?:\/\//i.test(
      clean
    )
  ) {
    return clean
  }

  if (
    clean.startsWith(
      'wkseller/'
    )
  ) {
    return `${siteOrigin}/image/${clean}`
  }

  if (
    clean.startsWith(
      'images/'
    )
  ) {
    return `${siteOrigin}/${clean}`
  }

  if (
    clean.startsWith(
      '/'
    )
  ) {
    return `${siteOrigin}${clean}`
  }

  return `${siteOrigin}/${clean}`
}

function resolvePublicAsset(
  src?: string | null
) {
  if (!src) return ''

  let clean = String(src)
    .trim()
    .replace(
      /\/images\/+\/?images\//i,
      '/images/'
    )

  if (/^https?:\/\//i.test(clean)) {
    return clean
  }

  if (clean.startsWith('/images/')) {
    return clean
  }

  if (clean.startsWith('images/')) {
    return `/${clean}`
  }

  if (clean.startsWith('/')) {
    return clean
  }

  return `/images/${clean.replace(/^\/+/, '')}`
}

function stripHtml(
  html?: string | null
) {
  if (!html) return ''

  if (
    typeof window ===
    'undefined'
  ) {
    return html.replace(
      /<[^>]+>/g,
      ' '
    )
  }

  const el =
    document.createElement(
      'div'
    )
  el.innerHTML = html

  return (
    el.textContent ||
    el.innerText ||
    ''
  )
}

function memberName(
  member: PublicMember
) {
  if (member.name) {
    return member.name
  }

  return [
    member.firstname,
    member.lastname,
  ]
    .filter(Boolean)
    .join(' ')
}

function getCommunityJoinLabel(
  community: any
) {
  const pricing =
    community?.pricing

  /*
   * FREE
   */
  if (
    !community
      ?.payment_required ||
    pricing?.mode ===
      'free'
  ) {
    return 'Unirme gratis'
  }

  /*
   * Tenemos comunidad de pago
   * pero no pudimos resolver precio.
   */
  const amount =
    Number(
      pricing?.amount
    )

  if (
    !Number.isFinite(
      amount
    ) ||
    amount <= 0
  ) {
    return 'Unirme'
  }

  const currency =
    String(
      pricing?.currency ||
        'USD'
    ).toUpperCase()

  const formattedAmount =
    new Intl.NumberFormat(
      'en-US',
      {
        style:
          'currency',

        currency,

        minimumFractionDigits:
          Number.isInteger(
            amount
          )
            ? 0
            : 2,

        maximumFractionDigits:
          Number.isInteger(
            amount
          )
            ? 0
            : 2,
      }
    ).format(
      amount
    )

  /*
   * ONE TIME
   */
  if (
    pricing?.mode ===
    'one_time'
  ) {
    return `Unirme ${formattedAmount}`
  }

  /*
   * RECURRING
   */
  const cycle =
    Math.max(
      1,
      Number(
        pricing?.cycle ||
          1
      )
    )

  switch (
    pricing?.frequency
  ) {
    case 'day':
      return cycle ===
        1
        ? `Unirme ${formattedAmount}/día`
        : `Unirme ${formattedAmount}/${cycle} días`

    case 'week':
      return cycle ===
        1
        ? `Unirme ${formattedAmount}/semana`
        : `Unirme ${formattedAmount}/${cycle} semanas`

    case 'year':
      return cycle ===
        1
        ? `Unirme ${formattedAmount}/año`
        : `Unirme ${formattedAmount}/${cycle} años`

    case 'month':
    default:
      return cycle ===
        1
        ? `Unirme ${formattedAmount}/mes`
        : `Unirme ${formattedAmount}/${cycle} meses`
  }
}

function getInitials(
  value?: string
) {
  return String(
    value || 'M'
  )
    .trim()
    .split(/\s+/)
    .map(
      (word) =>
        word[0] || ''
    )
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/* -------------------------------------------------------------------------- */
/*                              MAIN COMPONENT                                */
/* -------------------------------------------------------------------------- */

export function AboutPublicPage({
  community,
  isOwner,
  sellerId,
  access,
}: {
  community: any
  isOwner: boolean
  sellerId: number
  access: CommunityAccess
  header?: boolean
}) {
  const { brand } =
    useBranding(sellerId)

  const {
    loggedIn,
  } = useCurrentUser()

  const {
    isAdmin,
    loading:
      adminLoading,
  } = useIsAdmin()

  const {
    openImageManager,
  } = useImageManager()

  const searchParams =
    useSearchParams()

  /* ---------------------------------------------------------------------- */
  /*                              CORE STATE                                */
  /* ---------------------------------------------------------------------- */

  const [
    joinOpen,
    setJoinOpen,
  ] = useState(false)

  const [
    authSheetOpen,
    setAuthSheetOpen,
  ] = useState(false)

  const [
    view,
    setView,
  ] =
    useState<AuthView>(
      'signup'
    )

  const [
    startData,
    setStartData,
  ] =
    useState<StartPayload | null>(
      null
    )

  const [
    inviteErrorOpen,
    setInviteErrorOpen,
  ] =
    useState(false)

  const [
    modules,
    setModules,
  ] = useState<
    AboutModule[]
  >([])

  const [
    modsLoading,
    setModsLoading,
  ] = useState(false)

  const [
    pageEditorOpen,
    setPageEditorOpen,
  ] = useState(false)

  const [
    highlightEditorOpen,
    setHighlightEditorOpen,
  ] = useState(false)

  const [
    editingHighlight,
    setEditingHighlight,
  ] =
    useState<HighlightItem | null>(
      null
    )

  /* ---------------------------------------------------------------------- */
  /*                               IDENTITY                                 */
  /* ---------------------------------------------------------------------- */

  const {
    theme: resolvedTheme,
  } = useTheme()

  const name =
    brand?.name ||
    community?.name ||
    'Comunidad'

  const brandName =
    brand?.name ||
    community?.name ||
    'Comunidad'

  const resolvedLogoSrc =
    useMemo(
      () =>
        resolveBrandImage(
          brand?.logos
            ?.square ||
            community?.logo,
          API_CONFIG.BASE_URL
        ),
      [
        brand?.logos
          ?.square,
        community?.logo,
      ]
    )

  const resolvedHorizontalSrc =
    useMemo(() => {
      const logo =
        resolvedTheme ===
        'dark'
          ? brand?.logos
              ?.horizontal_dark ||
            brand?.logos
              ?.horizontal
          : brand?.logos
              ?.horizontal

      return resolveBrandImage(
        logo,
        API_CONFIG.BASE_URL
      )
    }, [
      resolvedTheme,
      brand?.logos
        ?.horizontal,
      brand?.logos
        ?.horizontal_dark,
    ])

  const fallbackHero =
    brand?.banner ||
    community?.banner_url ||
    null

  const memberCount =
    Number(
      community?.member_count ||
        0
    )

  const heroJoinRef =
    useRef<HTMLDivElement | null>(
      null
    )

  const joinLabel =
    useMemo(
      () =>
        getCommunityJoinLabel(
          community
        ),
      [
        community
          ?.payment_required,
        community
          ?.pricing,
      ]
    )

  const finalCtaRef =
    useRef<HTMLDivElement | null>(
      null
    )

  const [
    heroCtaVisible,
    setHeroCtaVisible,
  ] = useState(true)

  const [
    finalCtaVisible,
    setFinalCtaVisible,
  ] = useState(false)

  const showMobileStickyJoin =
    !heroCtaVisible &&
    !finalCtaVisible

  const approvalRequired =
    Number(
      community?.approval_required ||
        0
    ) === 1

  const paymentRequired =
    Number(
      community?.payment_required ||
        0
    ) === 1

  const isMember =
    access.status ===
    'approved'

  const isPending =
    access.status ===
    'pending'

  const nextUrl =
    resolveAuthNextFromStartPayload(
      startData
    )

  const shortDescription =
    String(
      community?.description ||
        ''
    ).trim()

  useEffect(() => {
    const target =
      heroJoinRef.current

    if (!target) {
      return
    }

    const observer =
      new IntersectionObserver(
        ([entry]) => {
          setHeroCtaVisible(
            entry.isIntersecting
          )
        },
        {
          threshold: 0.15,
        }
      )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const target =
      finalCtaRef.current

    if (!target) {
      return
    }

    const observer =
      new IntersectionObserver(
        ([entry]) => {
          setFinalCtaVisible(
            entry.isIntersecting
          )
        },
        {
          threshold: 0.15,
        }
      )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [])

  /* ---------------------------------------------------------------------- */
  /*                               GALLERY                                  */
  /* ---------------------------------------------------------------------- */

  const GALLERY_KEY =
    'media_gallery'

  const HIGHLIGHTS_KEY =
    'highlights'

  const SPACES_KEY =
    'spaces'

  const MEMBERS_KEY =
    'members'

  const [
    media,
    setMedia,
  ] = useState<MediaItem[]>(
    []
  )

  const [
    activeId,
    setActiveId,
  ] = useState<string | null>(
    null
  )

  const active =
    useMemo(
      () =>
        media.find(
          (item) =>
            item.id ===
            activeId
        ) ||
        media[0] ||
        null,
      [
        media,
        activeId,
      ]
    )

  const [
    canScrollDesktopLeft,
    setCanScrollDesktopLeft,
  ] = useState(false)

  const [
    canScrollDesktopRight,
    setCanScrollDesktopRight,
  ] = useState(false)

  const [
    videoOpen,
    setVideoOpen,
  ] = useState(false)

  const [
    videoUrl,
    setVideoUrl,
  ] = useState('')

  const [
    videoError,
    setVideoError,
  ] = useState<
    string | null
  >(null)

  const desktopCarouselRef =
    useRef<HTMLDivElement | null>(
      null
    )

  const mobileThumbsRef =
    useRef<HTMLDivElement | null>(
      null
    )

  const [
    lightboxIndex,
    setLightboxIndex,
  ] = useState<number | null>(
    null
  )

  const lightboxItem =
    lightboxIndex !== null
      ? media[lightboxIndex] || null
      : null

  function updateDesktopCarouselArrows() {
    const container =
      desktopCarouselRef.current

    if (!container) {
      return
    }

    const {
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = container

    setCanScrollDesktopLeft(
      scrollLeft > 4
    )

    setCanScrollDesktopRight(
      scrollLeft +
        clientWidth <
        scrollWidth - 4
    )
  }

  function openLightbox(
    item: MediaItem
  ) {
    const index =
      media.findIndex(
        mediaItem =>
          mediaItem.id === item.id
      )

    if (index < 0) {
      return
    }

    setLightboxIndex(
      index
    )
  }

  function closeLightbox() {
    setLightboxIndex(
      null
    )
  }

  function previousLightbox() {
    if (
      lightboxIndex === null ||
      !media.length
    ) {
      return
    }

    setLightboxIndex(
      (
        lightboxIndex -
          1 +
        media.length
      ) %
        media.length
    )
  }

  function nextLightbox() {
    if (
      lightboxIndex === null ||
      !media.length
    ) {
      return
    }

    setLightboxIndex(
      (
        lightboxIndex +
        1
      ) %
        media.length
    )
  }

  function scrollDesktopCarousel(
    direction:
      | 'previous'
      | 'next'
  ) {
    const container =
      desktopCarouselRef.current

    if (!container) {
      return
    }

    const amount =
      Math.max(
        320,
        container.clientWidth *
          0.72
      )

    container.scrollBy({
      left:
        direction === 'next'
          ? amount
          : -amount,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (
      lightboxIndex === null
    ) {
      return
    }

    function handleKeyDown(
      event: KeyboardEvent
    ) {
      if (
        event.key ===
        'Escape'
      ) {
        closeLightbox()
      }

      if (
        event.key ===
        'ArrowRight'
      ) {
        nextLightbox()
      }

      if (
        event.key ===
        'ArrowLeft'
      ) {
        previousLightbox()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown
    )

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      )
    }
  }, [
    lightboxIndex,
    media.length,
  ])

  useEffect(() => {
    const container =
      desktopCarouselRef.current

    if (!container) {
      return
    }

    updateDesktopCarouselArrows()

    container.addEventListener(
      'scroll',
      updateDesktopCarouselArrows,
      {
        passive: true,
      }
    )

    window.addEventListener(
      'resize',
      updateDesktopCarouselArrows
    )

    return () => {
      container.removeEventListener(
        'scroll',
        updateDesktopCarouselArrows
      )

      window.removeEventListener(
        'resize',
        updateDesktopCarouselArrows
      )
    }
  }, [media.length])

  /* ---------------------------------------------------------------------- */
  /*                                ABOUT                                   */
  /* ---------------------------------------------------------------------- */

  const [
    descEditing,
    setDescEditing,
  ] = useState(false)

  const [
    descSaving,
    setDescSaving,
  ] = useState(false)

  const [
    descDraft,
    setDescDraft,
  ] = useState(
    String(
      community?.about ||
        ''
    )
  )

  /* ---------------------------------------------------------------------- */
  /*                              MODULE DATA                               */
  /* ---------------------------------------------------------------------- */

  const galleryModule =
    useMemo(
      () =>
        modules.find(
          (module) =>
            module.module_key ===
            GALLERY_KEY
        ),
      [modules]
    )

  const highlightsModule =
    useMemo(
      () =>
        modules.find(
          (module) =>
            module.module_key ===
            HIGHLIGHTS_KEY
        ),
      [modules]
    )

  const spacesModule =
    useMemo(
      () =>
        modules.find(
          (module) =>
            module.module_key ===
            SPACES_KEY
        ),
      [modules]
    )

  const membersModule =
    useMemo(
      () =>
        modules.find(
          (module) =>
            module.module_key ===
            MEMBERS_KEY
        ),
      [modules]
    )

  const highlights =
    useMemo<
      HighlightItem[]
    >(() => {
      const items =
        highlightsModule
          ?.settings
          ?.items

      if (
        !Array.isArray(
          items
        )
      ) {
        return []
      }

      return items.filter(
        (item) =>
          item &&
          item.enabled !==
            false
      )
    }, [
      highlightsModule,
    ])

  const spaces =
    useMemo<
      PublicSpace[]
    >(() => {
      const source =
        community?.spaces ||
        community?.public_spaces ||
        community
          ?.featured_spaces ||
        []

      if (
        !Array.isArray(
          source
        )
      ) {
        return []
      }

      const selected =
        spacesModule
          ?.settings
          ?.selected_ids

      if (
        Array.isArray(
          selected
        ) &&
        selected.length
      ) {
        return source.filter(
          (space: any) => {
            const id =
              space.space_id ??
              space.category_id ??
              space.id

            return selected
              .map(String)
              .includes(
                String(id)
              )
          }
        )
      }

      return source
    }, [
      community,
      spacesModule,
    ])

  const members =
    useMemo<
      PublicMember[]
    >(() => {
      const source =
        community?.members ||
        community
          ?.public_members ||
        community
          ?.member_preview ||
        []

      return Array.isArray(
        source
      )
        ? source
        : []
    }, [community])

  const showHighlights =
    highlightsModule?.enabled === true &&
    highlights.length > 0

  const showSpaces =
    spacesModule?.enabled === true &&
    spaces.length > 0

  const showMembers =
    membersModule?.enabled === true &&
    (
      members.length > 0 ||
      memberCount > 0
    )

  /* ---------------------------------------------------------------------- */
  /*                              AUTH ERROR                                */
  /* ---------------------------------------------------------------------- */

  const authError =
    searchParams.get(
      'auth_error'
    )

  useEffect(() => {
    if (
      authError !==
      'invite_required'
    ) {
      return
    }

    setInviteErrorOpen(
      true
    )

    const url =
      new URL(
        window.location.href
      )

    url.searchParams.delete(
      'auth_error'
    )

    window.history.replaceState(
      {},
      '',
      url.toString()
    )
  }, [authError])

  /* ---------------------------------------------------------------------- */
  /*                               START                                    */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let alive = true

    ;(async () => {
      try {
        const res =
          await fetch(
            '/api/start',
            {
              cache:
                'no-store',
            }
          )

        if (!res.ok) {
          throw new Error(
            `HTTP_${res.status}`
          )
        }

        const json =
          (await res.json()) as StartPayload

        if (!alive) return

        setStartData(
          json
        )
      } catch {
        if (!alive) return

        setStartData(
          null
        )
      }
    })()

    return () => {
      alive = false
    }
  }, [])

  /* ---------------------------------------------------------------------- */
  /*                            LOAD MODULES                                */
  /* ---------------------------------------------------------------------- */

  function toRelImage(
    url: string
  ) {
    return String(
      url || ''
    )
      .replace(
        /^\/images\//,
        ''
      )
      .trim()
  }

  function toAbsImage(
    rel: string
  ) {
    if (!rel)
      return ''

    if (
      /^https?:\/\//i.test(
        rel
      )
    ) {
      return rel
    }

    if (
      rel.startsWith(
        '/images/'
      )
    ) {
      return rel
    }

    return `/images/${rel}`
  }

  useEffect(() => {
    let alive = true

    ;(async () => {
      setModsLoading(
        true
      )

      try {
        const res =
          await fetch(
            `/api/public/about-modules?seller_id=${sellerId}`,
            {
              cache:
                'no-store',
            }
          )

        const json =
          await res.json()

        if (!alive) return

        const nextModules =
          Array.isArray(
            json?.modules
          )
            ? json.modules
            : []

        setModules(
          nextModules
        )

        const gallery =
          nextModules.find(
            (module: any) =>
              module.module_key ===
              GALLERY_KEY
          )

        const items =
          gallery
            ?.settings
            ?.items

        if (
          Array.isArray(
            items
          ) &&
          items.length
        ) {
          const loaded: MediaItem[] =
            items.map(
              (
                item: any,
                index: number
              ) =>
                item?.type ===
                'video'
                  ? {
                      id: `db_vid_${index}`,
                      type: 'video',
                      url: String(
                        item.url ||
                          ''
                      ),
                    }
                  : {
                      id: `db_img_${index}`,
                      type: 'image',
                      src: toAbsImage(
                        String(
                          item.src ||
                            ''
                        )
                      ),
                    }
            )

          setMedia(
            loaded
          )

          const activeIndex =
            Number(
              gallery
                ?.settings
                ?.active || 0
            )

          const safeIndex =
            Math.min(
              Math.max(
                activeIndex,
                0
              ),
              loaded.length -
                1
            )

          setActiveId(
            loaded[
              safeIndex
            ]?.id || null
          )
        } else {
          setMedia([])
          setActiveId(null)
        }
      } finally {
        if (alive) {
          setModsLoading(
            false
          )
        }
      }
    })()

    return () => {
      alive = false
    }
  }, [sellerId])

  /* ---------------------------------------------------------------------- */
  /*                              ACTIONS                                   */
  /* ---------------------------------------------------------------------- */

  function openJoin() {
    if (
      loggedIn &&
      isMember
    ) {
      window.location.href =
        '/home'
      return
    }

    if (
      loggedIn &&
      !isMember
    ) {
      // Conservamos el comportamiento
      // público sin asumir endpoint adicional.
      setJoinOpen(true)
      setView('signup')
      return
    }

    setView('signup')

    if (
      window.innerWidth <
      1024
    ) {
      setAuthSheetOpen(
        true
      )
    } else {
      setJoinOpen(
        true
      )
    }
  }

  async function saveAbout() {
    if (!isOwner) return

    setDescSaving(
      true
    )

    try {
      await ajaxPost(
        '/api/community/about/update',
        {
          seller_id:
            sellerId,
          about:
            descDraft,
        }
      )

      community.about =
        descDraft

      setDescEditing(
        false
      )
    } finally {
      setDescSaving(
        false
      )
    }
  }

  async function saveGallery(
    nextMedia: MediaItem[],
    nextActiveId:
      | string
      | null
  ) {
    if (!isOwner) return

    const activeIndex =
      Math.max(
        0,
        nextMedia.findIndex(
          (item) =>
            item.id ===
            nextActiveId
        )
      )

    const items =
      nextMedia.map(
        (item) =>
          item.type ===
          'image'
            ? {
                type: 'image',
                src: toRelImage(
                  item.src
                ),
              }
            : {
                type: 'video',
                url: item.url,
              }
      )

    await ajaxPost(
      '/api/about-modules/update',
      {
        seller_id:
          sellerId,
        module_key:
          GALLERY_KEY,
        enabled: true,
        sort_order: 1,
        settings: {
          items,
          active:
            activeIndex,
        },
      }
    )
  }

  function addImage() {
    if (
      !isOwner ||
      !sellerId
    ) {
      return
    }

    openImageManager({
      sellerId,

      onSelect: (
        img: any
      ) => {
        const url =
          String(
            img?.url ||
              ''
          ).trim()

        if (
          !url.startsWith(
            '/images/'
          )
        ) {
          return
        }

        const item: MediaItem =
          {
            id: `img_${Date.now()}`,
            type: 'image',
            src: url,
          }

        setMedia(
          (prev) => {
            const next = [
              ...prev,
              item,
            ]

            saveGallery(
              next,
              item.id
            ).catch(
              console.warn
            )

            return next
          }
        )

        setActiveId(
          item.id
        )
      },
    })
  }

  function openAddVideo() {
    if (!isOwner) return

    setVideoUrl('')
    setVideoError(
      null
    )
    setVideoOpen(
      true
    )
  }

  function saveVideo() {
    const url =
      videoUrl.trim()

    if (!url) {
      setVideoError(
        'Pega una URL válida.'
      )
      return
    }

    if (
      !/^https?:\/\//i.test(
        url
      )
    ) {
      setVideoError(
        'La URL debe iniciar con http(s)://'
      )
      return
    }

    const item: MediaItem =
      {
        id: `vid_${Date.now()}`,
        type: 'video',
        url,
      }

    setMedia(
      (prev) => {
        const next = [
          ...prev,
          item,
        ]

        saveGallery(
          next,
          item.id
        ).catch(
          console.warn
        )

        return next
      }
    )

    setActiveId(
      item.id
    )

    setVideoOpen(
      false
    )
  }

  function removeMedia(
    id: string
  ) {
    setMedia(
      (prev) => {
        const next =
          prev.filter(
            (item) =>
              item.id !== id
          )

        const nextActive =
          activeId === id
            ? next[0]?.id ||
              null
            : activeId

        setActiveId(
          nextActive
        )

        saveGallery(
          next,
          nextActive
        ).catch(
          console.warn
        )

        return next
      }
    )
  }

  async function saveModule(
    moduleKey: string,
    enabled: boolean,
    sortOrder: number,
    settings: Record<
      string,
      any
    >
  ) {
    await ajaxPost(
      '/api/about-modules/update',
      {
        seller_id:
          sellerId,
        module_key:
          moduleKey,
        enabled,
        sort_order:
          sortOrder,
        settings,
      }
    )

    setModules(
      (prev) => {
        const exists =
          prev.some(
            (item) =>
              item.module_key ===
              moduleKey
          )

        if (!exists) {
          return [
            ...prev,
            {
              module_key:
                moduleKey,
              enabled,
              sort_order:
                sortOrder,
              settings,
            },
          ]
        }

        return prev.map(
          (item) =>
            item.module_key ===
            moduleKey
              ? {
                  ...item,
                  enabled,
                  sort_order:
                    sortOrder,
                  settings,
                }
              : item
        )
      }
    )
  }

  async function deleteHighlight(
    highlightId: string
  ) {
    const current =
      Array.isArray(
        highlightsModule
          ?.settings
          ?.items
      )
        ? highlightsModule!
            .settings
            .items
        : []

    const next =
      current.filter(
        (item: any) =>
          item.id !==
          highlightId
      )

    await saveModule(
      HIGHLIGHTS_KEY,
      true,
      10,
      {
        ...(highlightsModule
          ?.settings ||
          {}),
        items: next,
      }
    )
  }

  /* ---------------------------------------------------------------------- */
  /*                               RENDER                                   */
  /* ---------------------------------------------------------------------- */

  return (
    <>
      <main className="mx-auto w-full max-w-[1180px] px-4 pb-6 pt-4 sm:px-6 lg:px-8 lg:pb-12">

      {/* ================================================================ */}
      {/* PUBLIC TOP BAR                                                   */}
      {/* ================================================================ */}

      {!loggedIn && (
        <>
          {/* spacer SOLO mobile para compensar el header fixed */}
          <div className="h-[57px] lg:hidden" />

          <div
            className={cn(
              /* MOBILE */
              'fixed inset-x-0 top-0 z-50',
              'h-[57px]',
              'border-b border-border',
              'bg-background/95 backdrop-blur-xl',
              'lg:static lg:h-11',
              'lg:mb-4 lg:border-0',
              'lg:bg-transparent lg:backdrop-blur-none'
            )}
          >
            <div
              className={cn(
                'mx-auto flex h-full w-full items-center justify-between gap-4',
                'max-w-[1180px]',
                'px-4 sm:px-6 lg:px-0'
              )}
            >
              {/* COMMUNITY BRAND */}
              <div className="flex min-w-0 items-center">
                {resolvedHorizontalSrc ? (
                  <img
                    src={resolvedHorizontalSrc}
                    alt={brandName}
                    className="max-h-9 w-auto max-w-[180px] object-contain object-left sm:max-w-[230px]"
                  />
                ) : resolvedLogoSrc ? (
                  <div className="flex items-center gap-2.5">
                    <img
                      src={resolvedLogoSrc}
                      alt={brandName}
                      className="h-8 w-8 rounded-full object-cover"
                    />

                    <span className="truncate text-sm font-semibold">
                      {brandName}
                    </span>
                  </div>
                ) : (
                  <span className="truncate text-sm font-semibold">
                    {brandName}
                  </span>
                )}
              </div>

              {/* RIGHT */}
              <div className="flex shrink-0 items-center gap-2">
                {/* Hubee attribution ONLY desktop */}
                <div className="hidden">
                  <HubeeAttribution />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setView('login')

                    if (window.innerWidth < 1024) {
                      setAuthSheetOpen(true)
                    } else {
                      setJoinOpen(true)
                    }
                  }}
                  className={cn(
                    'inline-flex h-9 items-center justify-center',
                    'rounded-xl border border-border',
                    'bg-background px-4',
                    'text-xs font-semibold text-foreground',
                    'shadow-sm transition',
                    'hover:bg-muted/60',
                    'active:scale-[0.98]'
                  )}
                >
                  Log in
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* SELLER CONTROLS                                                  */}
      {/* ================================================================ */}

        {isOwner && (
          <div className="mb-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() =>
                setDescEditing(
                  true
                )
              }
              className={cn(
                'inline-flex h-9 items-center gap-2 rounded-full',
                'border border-border bg-background px-4',
                'text-xs font-medium text-foreground',
                'shadow-sm transition hover:bg-muted'
              )}
            >
              <Pencil className="h-3.5 w-3.5" />
              Editar descripción
            </button>

            <button
              type="button"
              onClick={() =>
                setPageEditorOpen(
                  true
                )
              }
              className={cn(
                'inline-flex h-9 items-center gap-2 rounded-full',
                'border border-border bg-background px-4',
                'text-xs font-medium text-foreground',
                'shadow-sm transition hover:bg-muted'
              )}
            >
              <Pencil className="h-3.5 w-3.5" />
              Editar página
            </button>
          </div>
        )}
        
        {/* ================================================================ */}
        {/* HERO IDENTITY                                                    */}
        {/* ================================================================ */}

        <section
          className={cn(
            'hidden lg:block',
            'relative overflow-hidden rounded-[26px]',
            'border border-border bg-card',
            'shadow-sm'
          )}
        >
          <div className="relative grid min-h-[230px] items-stretch lg:grid-cols-[1.12fr_.88fr]">

            {/* LEFT */}
            <div className="relative z-10 flex flex-col justify-center p-6 sm:p-8 lg:p-9">
              
              <div className="flex items-start gap-5">
                
                {/* LOGO */}
                {resolvedLogoSrc ? (
                  <div
                    className={cn(
                      'flex shrink-0 items-center justify-center',
                      'h-[88px] w-[88px]',
                      'sm:h-[104px] sm:w-[104px]',
                      'overflow-hidden rounded-full',
                      'border border-border',
                      'bg-background',
                      'shadow-lg'
                    )}
                  >
                    <img
                      src={resolvedLogoSrc}
                      alt={brandName}
                      className="h-full w-full rounded-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex shrink-0 items-center justify-center',
                      'h-[88px] w-[88px]',
                      'sm:h-[104px] sm:w-[104px]',
                      'rounded-full',
                      'border border-border',
                      'bg-muted',
                      'text-2xl font-bold'
                    )}
                  >
                    {getInitials(name)}
                  </div>
                )}

                {/* TITLE + DESCRIPTION + PILLS */}
                <div className="min-w-0 flex-1 pt-1">
                  <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                    {name}
                  </h1>

                  {shortDescription ? (
                    <p className="mt-1.5 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-[15px]">
                      {shortDescription}
                    </p>
                  ) : null}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <HeroPill
                      icon={
                        approvalRequired ? (
                          <LockKeyhole className="h-3.5 w-3.5" />
                        ) : (
                          <Globe2 className="h-3.5 w-3.5" />
                        )
                      }
                    >
                      {approvalRequired
                        ? 'Privada'
                        : 'Pública'}
                    </HeroPill>

                    <HeroPill>
                      {paymentRequired
                        ? 'De pago'
                        : 'Gratis'}
                    </HeroPill>

                    <HeroPill
                      icon={
                        <Users className="h-3.5 w-3.5" />
                      }
                    >
                      {formatCompactCount(
                        memberCount
                      )}{' '}
                      miembros
                    </HeroPill>
                  </div>
                </div>
              </div>

              {/* CTA + MEMBER PROOF */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                {!isOwner && (
                  <div ref={heroJoinRef}>
                    <PrimaryJoinButton
                      loggedIn={loggedIn}
                      isMember={isMember}
                      isPending={isPending}
                      joinLabel={joinLabel}
                      onClick={openJoin}
                    />
                  </div>
                )}

                <MiniMemberProof
                  members={members}
                  memberCount={memberCount}
                />
              </div>
            </div>

            {/* RIGHT VISUAL */}
            {fallbackHero && (
              <div
                className={cn(
                  'absolute inset-y-0 right-0 hidden lg:block',
                  'w-[43%] overflow-hidden'
                )}
              >
                <img
                  src={resolvePublicAsset(fallbackHero)}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />

                <div
                  className={cn(
                    'absolute inset-0',
                    'bg-gradient-to-r',
                    'from-card via-card/20 to-transparent'
                  )}
                />
              </div>
            )}
          </div>
        </section>

        {/* ================================================================ */}
        {/* MOBILE COMMUNITY TITLE                                           */}
        {/* ================================================================ */}

        <div className="mt-5 lg:hidden">
          <h1 className="text-xl font-bold tracking-tight text-foreground">
            {name}
          </h1>
        </div>

        {/* ================================================================ */}
        {/* PUBLIC MEDIA GALLERY                                             */}
        {/* ================================================================ */}

        {(media.length > 0 ||
          isOwner) && (
          <section className="mt-4">

            {/* -------------------------------------------------------------- */}
            {/* MOBILE                                                         */}
            {/* Main media + horizontal selector                               */}
            {/* -------------------------------------------------------------- */}

            <div className="lg:hidden">
              <div
                className={cn(
                  'relative overflow-hidden',
                  'rounded-[24px]',
                  'border border-border',
                  'bg-black',
                  'shadow-sm'
                )}
              >
                {active ? (
                  active.type ===
                  'video' ? (
                    <div
                      className={cn(
                        'relative w-full overflow-hidden',
                        'bg-black'
                      )}
                    >
                      <div className="aspect-video w-full">
                        <VideoEmbed
                          url={active.url}
                          rounded="none"
                          variant="glass"
                          className="h-full w-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        openLightbox(
                          active
                        )
                      }
                      className="group relative flex min-h-[280px] w-full items-center justify-center bg-black"
                    >
                      <GalleryMediaPreview
                        item={active}
                        mode="mobile-main"
                      />

                      <div
                        className={cn(
                          'pointer-events-none absolute right-3 top-3',
                          'flex h-9 w-9 items-center justify-center',
                          'rounded-full bg-black/55 text-white',
                          'backdrop-blur-md'
                        )}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </div>
                    </button>
                  )
                ) : (
                  <div className="flex aspect-video items-center justify-center text-sm text-white/60">
                    Agrega una imagen o video
                  </div>
                )}
              </div>

              {/* mobile carousel */}
              <div
                ref={
                  mobileThumbsRef
                }
                className={cn(
                  'mt-2.5 flex gap-2.5',
                  'overflow-x-auto pb-1',
                  'scrollbar-none',
                  'snap-x snap-mandatory'
                )}
              >
                {media.map(
                  item => {
                    const selected =
                      item.id ===
                      active?.id

                    return (
                      <div
                        key={
                          item.id
                        }
                        className="relative shrink-0 snap-start"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setActiveId(
                              item.id
                            )

                            saveGallery(
                              media,
                              item.id
                            ).catch(
                              console.warn
                            )
                          }}
                          className={cn(
                            'relative flex h-[92px] min-w-[92px]',
                            'max-w-[160px] items-center justify-center',
                            'overflow-hidden rounded-[16px]',
                            'border bg-black',
                            'transition',
                            selected
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-border'
                          )}
                        >
                          <GalleryMediaPreview
                            item={item}
                            mode="thumbnail"
                          />
                        </button>

                        {isOwner && (
                          <button
                            type="button"
                            onClick={() =>
                              removeMedia(
                                item.id
                              )
                            }
                            className={cn(
                              'absolute right-1.5 top-1.5',
                              'flex h-6 w-6 items-center justify-center',
                              'rounded-full bg-black/70',
                              'text-white backdrop-blur'
                            )}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )
                  }
                )}

                {isOwner && (
                  <div className="flex h-[92px] shrink-0 items-center gap-1 rounded-[16px] border border-dashed border-border px-2">
                    <button
                      type="button"
                      onClick={
                        addImage
                      }
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <ImagePlus className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={
                        openAddVideo
                      }
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Video className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* DESKTOP                                                        */}
            {/* One adaptive carousel. No featured image.                       */}
            {/* -------------------------------------------------------------- */}

            <div
              className={cn(
                'relative hidden lg:block',
                'rounded-[26px]',
                'border border-border',
                'bg-card',
                'p-2.5',
                'shadow-sm'
              )}
            >
              {media.length > 0 ? (
                <>
                  <div
                    ref={
                      desktopCarouselRef
                    }
                    className={cn(
                      'flex gap-3 overflow-x-auto',
                      'scroll-smooth',
                      'scrollbar-none',
                      'snap-x snap-mandatory'
                    )}
                  >
                    {media.map(
                      item => (
                        <div
                          key={
                            item.id
                          }
                          className="group relative shrink-0 snap-start"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              openLightbox(
                                item
                              )
                            }
                            className={cn(
                              'relative flex h-[390px]',
                              'min-w-[220px] max-w-[700px]',
                              'items-center justify-center',
                              'overflow-hidden rounded-[20px]',
                              'border border-border',
                              'bg-black'
                            )}
                          >
                            <GalleryMediaPreview
                              item={item}
                              mode="desktop-carousel"
                            />

                            <div
                              className={cn(
                                'pointer-events-none absolute inset-0',
                                'bg-black/0 transition',
                                'group-hover:bg-black/5'
                              )}
                            />

                            <div
                              className={cn(
                                'pointer-events-none absolute right-3 top-3',
                                'flex h-9 w-9 items-center justify-center',
                                'rounded-full bg-black/55',
                                'text-white backdrop-blur-md',
                                'opacity-0 transition',
                                'group-hover:opacity-100'
                              )}
                            >
                              <Maximize2 className="h-4 w-4" />
                            </div>
                          </button>

                          {isOwner && (
                            <button
                              type="button"
                              onClick={() =>
                                removeMedia(
                                  item.id
                                )
                              }
                              className={cn(
                                'absolute right-3 bottom-3',
                                'flex h-8 w-8 items-center justify-center',
                                'rounded-full bg-black/65',
                                'text-white backdrop-blur-md',
                                'opacity-0 transition',
                                'group-hover:opacity-100'
                              )}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      )
                    )}

                    {isOwner && (
                      <div
                        className={cn(
                          'flex h-[390px] w-[220px]',
                          'shrink-0 snap-start',
                          'flex-col items-center justify-center gap-3',
                          'rounded-[20px]',
                          'border border-dashed border-border',
                          'bg-muted/20'
                        )}
                      >
                        <button
                          type="button"
                          onClick={
                            addImage
                          }
                          className="flex items-center gap-2 rounded-xl bg-muted px-4 py-2.5 text-sm font-medium hover:bg-muted/80"
                        >
                          <ImagePlus className="h-4 w-4" />
                          Imagen
                        </button>

                        <button
                          type="button"
                          onClick={
                            openAddVideo
                          }
                          className="flex items-center gap-2 rounded-xl bg-muted px-4 py-2.5 text-sm font-medium hover:bg-muted/80"
                        >
                          <Video className="h-4 w-4" />
                          Video
                        </button>
                      </div>
                    )}
                  </div>

                  {/* left */}
                  {media.length > 1 &&
                  canScrollDesktopLeft && (
                    <button
                      type="button"
                      aria-label="Anterior"
                      onClick={() =>
                        scrollDesktopCarousel(
                          'previous'
                        )
                      }
                      className={cn(
                        'absolute left-5 top-1/2 z-10',
                        '-translate-y-1/2',
                        'flex h-11 w-11 items-center justify-center',
                        'rounded-full bg-black/65',
                        'text-white shadow-xl',
                        'backdrop-blur-md',
                        'transition hover:bg-black/80'
                      )}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                  )}

                  {/* right */}
                  {media.length > 1 &&
                  canScrollDesktopRight && (
                    <button
                      type="button"
                      aria-label="Siguiente"
                      onClick={() =>
                        scrollDesktopCarousel(
                          'next'
                        )
                      }
                      className={cn(
                        'absolute right-5 top-1/2 z-10',
                        '-translate-y-1/2',
                        'flex h-11 w-11 items-center justify-center',
                        'rounded-full bg-black/65',
                        'text-white shadow-xl',
                        'backdrop-blur-md',
                        'transition hover:bg-black/80'
                      )}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  )}
                </>
              ) : (
                isOwner && (
                  <div className="flex aspect-[16/5] items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={
                        addImage
                      }
                      className="flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm font-medium"
                    >
                      <ImagePlus className="h-4 w-4" />
                      Agregar imagen
                    </button>

                    <button
                      type="button"
                      onClick={
                        openAddVideo
                      }
                      className="flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm font-medium"
                    >
                      <Video className="h-4 w-4" />
                      Agregar video
                    </button>
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {/* ================================================================ */}
        {/* MOBILE COMMUNITY META                                            */}
        {/* ================================================================ */}

        <div className="mt-3 flex items-center justify-between gap-3 lg:hidden">
          <div className="flex min-w-0 items-center gap-2">

            <MobileCommunityPill
              icon={
                approvalRequired ? (
                  <LockKeyhole className="h-3.5 w-3.5" />
                ) : (
                  <Globe2 className="h-3.5 w-3.5" />
                )
              }
            >
              {approvalRequired
                ? 'Privada'
                : 'Pública'}
            </MobileCommunityPill>

            {paymentRequired && (
              <MobileCommunityPill>
                De pago
              </MobileCommunityPill>
            )}
          </div>

          <MobileMemberProof
            members={members}
            memberCount={memberCount}
          />
        </div>

        {/* ================================================================ */}
        {/* ABOUT                                                            */}
        {/* ================================================================ */}

        {(community?.about ||
          isOwner) && (
          <section
            className={cn(
              'mt-5',

              /* MOBILE: completamente libre */
              'bg-transparent px-0 py-0',

              /* DESKTOP: conserva exactamente el card */
              'lg:rounded-[22px]',
              'lg:border lg:border-border',
              'lg:bg-card',
              'lg:px-6 lg:py-5',
              'lg:shadow-sm'
            )}
          >
            {!descEditing ? (
              <>
                <h2 className="hidden text-lg font-semibold tracking-tight lg:block">
                  {name}
                </h2>

                {community?.about ? (
                  <div
                    className={cn(
                      'max-w-none text-sm leading-6 text-muted-foreground',
                      'lg:mt-2',
                      '[&_strong]:font-semibold [&_strong]:text-foreground',
                      '[&_p]:my-2',
                      '[&_ul]:my-2 [&_ul]:space-y-1 [&_ul]:pl-5',
                      '[&_ol]:my-2 [&_ol]:space-y-1 [&_ol]:pl-5',
                      '[&_li]:pl-1'
                    )}
                    dangerouslySetInnerHTML={{
                      __html:
                        String(
                          community.about
                        ),
                    }}
                  />
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Agrega una descripción para explicar por qué alguien debería unirse.
                  </p>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">
                    Descripción de la comunidad
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Explica de forma breve qué encontrará alguien al entrar.
                  </p>
                </div>

                <RichTextEditor
                  content={
                    descDraft
                  }
                  onChange={(
                    html
                  ) =>
                    setDescDraft(
                      html
                    )
                  }
                />

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={
                      descSaving
                    }
                    onClick={() =>
                      setDescEditing(
                        false
                      )
                    }
                    className="rounded-xl border border-border px-4 py-2 text-sm"
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    disabled={
                      descSaving
                    }
                    onClick={
                      saveAbout
                    }
                    className="rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background"
                  >
                    {descSaving
                      ? 'Guardando…'
                      : 'Guardar'}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ================================================================ */}
        {/* HIGHLIGHTS                                                       */}
        {/* ================================================================ */}

        {(showHighlights ||
          isOwner) && (
          <section className="mt-5">
            <SectionHeading
              title="Destacados"
              action={
                isOwner
                  ? {
                      label:
                        'Agregar',
                      onClick:
                        () => {
                          setEditingHighlight(
                            null
                          )
                          setHighlightEditorOpen(
                            true
                          )
                        },
                    }
                  : undefined
              }
            />

            {showHighlights ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {highlights
                  .slice(
                    0,
                    8
                  )
                  .map(
                    (
                      highlight
                    ) => (
                      <HighlightCard
                        key={
                          highlight.id
                        }
                        item={
                          highlight
                        }
                        isOwner={
                          isOwner
                        }
                        onEdit={() => {
                          setEditingHighlight(
                            highlight
                          )
                          setHighlightEditorOpen(
                            true
                          )
                        }}
                        onDelete={() =>
                          deleteHighlight(
                            highlight.id
                          )
                        }
                      />
                    )
                  )}
              </div>
            ) : (
              isOwner && (
                <EmptyEditorCard
                  title="Agrega tu primer destacado"
                  description="Video de bienvenida, recursos gratuitos, WhatsApp, YouTube o cualquier enlace que ayude a convencer al visitante."
                  onClick={() => {
                    setEditingHighlight(
                      null
                    )
                    setHighlightEditorOpen(
                      true
                    )
                  }}
                />
              )
            )}
          </section>
        )}

        {/* ================================================================ */}
        {/* SPACES                                                           */}
        {/* ================================================================ */}

        {(showSpaces ||
          isOwner) && (
          <section className="mt-5">
            <SectionHeading
              title="Explora la comunidad"
            />

            {showSpaces ? (
              <>
                {/* MOBILE — horizontal carousel */}
                <div
                  className={cn(
                    'flex gap-3 overflow-x-auto pb-1',
                    'snap-x snap-mandatory',
                    'scrollbar-none',
                    'lg:hidden'
                  )}
                >
                  {spaces.map(
                    (space, index) => (
                      <div
                        key={String(
                          space.space_id ??
                            space.category_id ??
                            space.id ??
                            index
                        )}
                        className={cn(
                          'shrink-0 snap-start',
                          // 80% ≈ una card completa + 25% de la siguiente
                          'w-[80%]'
                        )}
                      >
                        <PublicSpaceCard
                          space={space}
                          onJoin={openJoin}
                          isMember={isMember}
                        />
                      </div>
                    )
                  )}
                </div>

                {/* DESKTOP */}
                <div className="hidden gap-3 overflow-hidden lg:flex">
                  {spaces
                    .slice(0, 4)
                    .map((space, index) => {
                      const isLastVisibleCard =
                        index === 3 &&
                        spaces.length > 4

                      return (
                        <div
                          key={String(
                            space.space_id ??
                              space.category_id ??
                              space.id ??
                              index
                          )}
                          className="w-[calc(25%-9px)] shrink-0"
                        >
                          <PublicSpaceCard
                            space={space}
                            onJoin={openJoin}
                            isMember={isMember}
                            fadeRight={isLastVisibleCard}
                          />
                        </div>
                      )
                    })}
                </div>
              </>
            ) : (
              isOwner && (
                <EmptyPreviewCard
                  title="Espacios"
                  description="Los Espacios públicos de esta comunidad aparecerán aquí."
                />
              )
            )}
          </section>
        )}

        {/* ================================================================ */}
        {/* MEMBERS                                                          */}
        {/* ================================================================ */}

        {showMembers && (
          <section className="mt-5">
            <div className="rounded-[22px] border border-border bg-card px-5 py-4 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="shrink-0">
                  <h2 className="text-base font-semibold tracking-tight">
                    Miembros
                  </h2>

                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {memberCount >
                    0
                      ? `Más de ${memberCount.toLocaleString(
                          'es-MX'
                        )} personas ya están dentro`
                      : 'Conoce a algunas personas de la comunidad'}
                  </p>
                </div>

                <MemberAvatarRow
                  members={
                    members
                  }
                  memberCount={
                    memberCount
                  }
                />
              </div>
            </div>
          </section>
        )}

        {/* ================================================================ */}
        {/* FINAL CTA                                                        */}
        {/* ================================================================ */}

        {!isOwner && (
          <div ref={finalCtaRef}>
          <section
            className={cn(
              'relative mt-5 overflow-hidden rounded-[24px]',
              'border border-primary/30 bg-card',
              'shadow-sm'
            )}
          >
            {fallbackHero && (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-[0.36]"
                  style={{
                    backgroundImage: `url("${resolvePublicAsset(
                      fallbackHero
                    )}")`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/75" />
              </>
            )}

            <div className="relative flex flex-col gap-5 px-6 py-6 sm:flex-row sm:items-center sm:justify-between lg:px-8">
              <div className="flex items-center gap-4">
                {resolvedLogoSrc ? (
                  <img
                    src={
                      resolvedLogoSrc
                    }
                    alt=""
                    className="h-12 w-12 rounded-2xl object-contain"
                  />
                ) : null}

                <div>
                  <h2 className="text-xl font-bold tracking-tight">
                    ¿Listo para entrar a {name}?
                  </h2>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Únete y descubre todo lo que la comunidad tiene para ti.
                  </p>
                </div>
              </div>

              <div className="shrink-0">
                <PrimaryJoinButton
                  loggedIn={loggedIn}
                  isMember={isMember}
                  isPending={isPending}
                  joinLabel={joinLabel}
                  onClick={openJoin}
                  large
                />
              </div>
            </div>
          </section>
          </div>
        )}

        {!loggedIn && (
          <footer className="mt-8 border-t border-border/60 pt-4">

            <div className="flex items-center justify-between gap-4 text-[11px] text-muted-foreground">
              <HubeeAttribution />

              <div className="flex items-center gap-1.5">
                <a
                  href="/privacy"
                  className="transition hover:text-foreground"
                >
                  Privacy
                </a>

                <span>•</span>

                <a
                  href="/terms"
                  className="transition hover:text-foreground"
                >
                  Terms
                </a>
              </div>
            </div>
          </footer>
        )}

        {modsLoading && (
          <div className="mt-4 text-center text-xs text-muted-foreground">
            Cargando página…
          </div>
        )}
      </main>

      {/* ================================================================== */}
      {/* PAGE EDITOR                                                       */}
      {/* ================================================================== */}

      <Dialog
        open={
          pageEditorOpen
        }
        onOpenChange={
          setPageEditorOpen
        }
      >
        <DialogContent className="max-w-lg overflow-hidden p-0">
          <PageVisibilityEditor
            modules={
              modules
            }
            onSave={async ({
              highlights,
              spaces,
              members,
            }) => {
              await saveModule(
                HIGHLIGHTS_KEY,
                highlights,
                10,
                highlightsModule
                  ?.settings ||
                  { items: [] }
              )

              await saveModule(
                SPACES_KEY,
                spaces,
                20,
                spacesModule
                  ?.settings ||
                  {}
              )

              await saveModule(
                MEMBERS_KEY,
                members,
                30,
                membersModule
                  ?.settings ||
                  {}
              )

              setPageEditorOpen(
                false
              )
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* HIGHLIGHT EDITOR                                                  */}
      {/* ================================================================== */}

      <Dialog
        open={
          highlightEditorOpen
        }
        onOpenChange={
          setHighlightEditorOpen
        }
      >
        <DialogContent className="max-w-lg">
          <HighlightEditor
            sellerId={
              sellerId
            }
            initial={
              editingHighlight
            }
            openImageManager={
              openImageManager
            }
            onCancel={() =>
              setHighlightEditorOpen(
                false
              )
            }
            onSave={async (
              item
            ) => {
              const current =
                Array.isArray(
                  highlightsModule
                    ?.settings
                    ?.items
                )
                  ? highlightsModule!
                      .settings
                      .items
                  : []

              const exists =
                current.some(
                  (
                    currentItem: any
                  ) =>
                    currentItem.id ===
                    item.id
                )

              const next =
                exists
                  ? current.map(
                      (
                        currentItem: any
                      ) =>
                        currentItem.id ===
                        item.id
                          ? item
                          : currentItem
                    )
                  : [
                      ...current,
                      item,
                    ]

              await saveModule(
                HIGHLIGHTS_KEY,
                true,
                10,
                {
                  ...(highlightsModule
                    ?.settings ||
                    {}),
                  items:
                    next,
                }
              )

              setHighlightEditorOpen(
                false
              )
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* VIDEO URL                                                         */}
      {/* ================================================================== */}

      <Dialog
        open={videoOpen}
        onOpenChange={
          setVideoOpen
        }
      >
        <DialogContent className="max-w-lg">
          <div className="space-y-4">
            <div>
              <div className="text-lg font-semibold">
                Agregar video
              </div>

              <div className="mt-1 text-sm text-muted-foreground">
                Pega una URL de YouTube, Vimeo u otro proveedor soportado.
              </div>
            </div>

            <div className="space-y-2">
              <input
                value={
                  videoUrl
                }
                onChange={(
                  event
                ) => {
                  setVideoUrl(
                    event
                      .target
                      .value
                  )
                  setVideoError(
                    null
                  )
                }}
                placeholder="https://..."
                className={cn(
                  'w-full rounded-xl border bg-background px-3 py-2.5 text-sm outline-none',
                  videoError
                    ? 'border-destructive'
                    : 'border-border'
                )}
              />

              {videoError && (
                <div className="text-xs text-destructive">
                  {
                    videoError
                  }
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  setVideoOpen(
                    false
                  )
                }
                className="rounded-xl border border-border px-4 py-2 text-sm"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={
                  saveVideo
                }
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background"
              >
                <Check className="h-4 w-4" />
                Guardar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* MOBILE JOIN                                                       */}
      {/* ================================================================== */}

      {!loggedIn && (
        <>
          
        <AnimatePresence>
          {showMobileStickyJoin &&
            !authSheetOpen && (
              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 20,
                }}
                transition={{
                  duration: 0.18,
                }}
                className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 lg:hidden"
              >
                <div className="mx-auto max-w-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setView(
                        'signup'
                      )
                      setAuthSheetOpen(
                        true
                      )
                    }}
                    className="h-12 w-full rounded-2xl bg-primary font-semibold text-primary-foreground shadow-xl active:scale-[0.99]"
                  >
                    {joinLabel}
                  </button>
                </div>
              </motion.div>
            )}
        </AnimatePresence>

          <AnimatePresence>
            {authSheetOpen && (
              <>
                <motion.button
                  type="button"
                  aria-label="Cerrar"
                  className="fixed inset-0 z-40 bg-black/60 lg:hidden"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  onClick={() =>
                    setAuthSheetOpen(
                      false
                    )
                  }
                />

                <motion.div
                  className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl border border-border bg-background shadow-2xl lg:hidden"
                  initial={{
                    y: '100%',
                  }}
                  animate={{
                    y: 0,
                  }}
                  exit={{
                    y: '100%',
                  }}
                  transition={{
                    type: 'spring',
                    stiffness:
                      380,
                    damping: 38,
                  }}
                >
                  <div className="mx-auto w-full max-w-lg">
                    <div className="px-5 pb-4 pt-3">
                      <div className="mx-auto h-1.5 w-10 rounded-full bg-foreground/15" />

                      <div className="mt-4 flex items-start justify-between gap-4">
                        <div>
                          <div className="text-xl font-semibold tracking-tight">
                            {view ===
                            'login'
                              ? 'Bienvenido de nuevo'
                              : `Únete a ${name}`}
                          </div>

                          <div className="mt-1 text-sm text-muted-foreground">
                            {view ===
                            'login' ? (
                              <>
                                ¿Aún no tienes cuenta?{' '}
                                <button
                                  type="button"
                                  className="text-foreground underline underline-offset-4"
                                  onClick={() =>
                                    setView(
                                      'signup'
                                    )
                                  }
                                >
                                  Regístrate
                                </button>
                              </>
                            ) : (
                              <>
                                ¿Ya tienes cuenta?{' '}
                                <button
                                  type="button"
                                  className="text-foreground underline underline-offset-4"
                                  onClick={() =>
                                    setView(
                                      'login'
                                    )
                                  }
                                >
                                  Inicia sesión
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setAuthSheetOpen(
                              false
                            )
                          }
                          className="rounded-full bg-muted px-3 py-1.5 text-sm"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>

                    <div className="max-h-[75dvh] overflow-auto px-5 pb-6">
                      <div className="mt-4">
                        {view ===
                        'login' ? (
                          <AuthCard
                            sellerId={
                              sellerId
                            }
                            variant="inline"
                            chrome="none"
                            theme={resolvedTheme}
                            next={
                              nextUrl
                            }
                            onRequestSignup={() =>
                              setView(
                                'signup'
                              )
                            }
                          />
                        ) : (
                          <AuthSignupCard
                            companyStatus={
                              community?.approval_required
                                ? 1
                                : 0
                            }
                            companyJoinMode={
                              community?.company_join_mode ||
                              'open'
                            }
                            isWhitelabel={
                              !!community?.is_whitelabel
                            }
                            sellerId={
                              sellerId
                            }
                            variant="inline"
                            chrome="none"
                            theme={resolvedTheme}
                            next={
                              nextUrl
                            }
                            communityName={
                              name
                            }
                            communityAvatarUrl={
                              resolvedLogoSrc ||
                              null
                            }
                            onRequestLogin={() =>
                              setView(
                                'login'
                              )
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}

      {/* ================================================================== */}
      {/* DESKTOP AUTH MODAL                                                 */}
      {/* ================================================================== */}

      <AuthModal
        open={joinOpen}
        onOpenChange={
          setJoinOpen
        }
        sellerId={
          sellerId
        }
        initialView={
          view
        }
        companyStatus={
          community?.company_status ??
          community?.approval_required
        }
        companyJoinMode={
          community?.company_join_mode ||
          'open'
        }
        isWhitelabel={
          !!community?.is_whitelabel
        }
        communityName={
          name
        }
        communityAvatarUrl={
          resolvedLogoSrc ||
          null
        }
        chrome="glass"
        theme={resolvedTheme}
        next={nextUrl}
      />

      {/* ================================================================== */}
      {/* INVITE ONLY                                                       */}
      {/* ================================================================== */}

      <Dialog
        open={
          inviteErrorOpen
        }
        onOpenChange={
          setInviteErrorOpen
        }
      >
        <DialogContent
          className="max-w-md rounded-3xl"
          hideCloseButton
        >
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-muted">
              <LockKeyhole className="h-6 w-6 text-muted-foreground" />
            </div>

            <h3 className="mt-6 text-xl font-semibold">
              Acceso restringido
            </h3>

            <p className="mt-3 text-sm text-muted-foreground">
              Esta comunidad es solo por invitación.
            </p>

            <p className="mt-2 text-sm text-muted-foreground">
              Necesitas un enlace de invitación para crear una cuenta.
            </p>

            <button
              type="button"
              onClick={() =>
                setInviteErrorOpen(
                  false
                )
              }
              className="mt-6 w-full rounded-2xl bg-foreground px-4 py-3 text-sm font-semibold text-background"
            >
              Entendido
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* MEDIA LIGHTBOX                                                     */}
      {/* ================================================================== */}

      <AnimatePresence>
        {lightboxItem && (
          <motion.div
            className="fixed inset-0 z-[10000] bg-black/95"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
          >
            {/* click background */}
            <button
              type="button"
              aria-label="Cerrar galería"
              onClick={
                closeLightbox
              }
              className="absolute inset-0"
            />

            {/* header */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center justify-between p-4 sm:p-6">
              <div className="pointer-events-auto rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white/80 backdrop-blur-md">
                {(lightboxIndex ??
                  0) +
                  1}{' '}
                / {media.length}
              </div>

              <button
                type="button"
                onClick={
                  closeLightbox
                }
                className={cn(
                  'pointer-events-auto',
                  'flex h-11 w-11 items-center justify-center',
                  'rounded-full bg-black/55',
                  'text-white backdrop-blur-md',
                  'transition hover:bg-white/15'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* content */}
            <div
              className={cn(
                'pointer-events-none absolute inset-0',
                'flex items-center justify-center',
                'px-4 py-20 sm:px-20 sm:py-16'
              )}
            >
              <motion.div
                key={
                  lightboxItem.id
                }
                initial={{
                  opacity: 0,
                  scale: 0.97,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.97,
                }}
                transition={{
                  duration: 0.18,
                }}
                className={cn(
                  'pointer-events-auto',
                  'flex max-h-full max-w-full',
                  'items-center justify-center'
                )}
              >
                {lightboxItem.type ===
                'image' ? (
                  <img
                    src={
                      lightboxItem.src
                    }
                    alt=""
                    className={cn(
                      'block',
                      'max-h-[calc(100dvh-8rem)]',
                      'max-w-[calc(100vw-2rem)]',
                      'sm:max-w-[calc(100vw-10rem)]',
                      'h-auto w-auto',
                      'object-contain'
                    )}
                  />
                ) : (
                  <div
                    className={cn(
                      'aspect-video',
                      'w-[min(1200px,calc(100vw-2rem))]',
                      'sm:w-[min(1200px,calc(100vw-10rem))]',
                      'overflow-hidden rounded-xl bg-black'
                    )}
                  >
                    <VideoEmbed
                      url={
                        lightboxItem.url
                      }
                      rounded="none"
                      variant="glass"
                      className="h-full w-full"
                    />
                  </div>
                )}
              </motion.div>
            </div>

            {/* previous */}
            {media.length >
              1 && (
              <button
                type="button"
                aria-label="Anterior"
                onClick={
                  previousLightbox
                }
                className={cn(
                  'absolute left-3 top-1/2 z-30',
                  '-translate-y-1/2',
                  'flex h-11 w-11 items-center justify-center',
                  'rounded-full bg-black/55',
                  'text-white backdrop-blur-md',
                  'transition hover:bg-white/15',
                  'sm:left-6'
                )}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            {/* next */}
            {media.length >
              1 && (
              <button
                type="button"
                aria-label="Siguiente"
                onClick={
                  nextLightbox
                }
                className={cn(
                  'absolute right-3 top-1/2 z-30',
                  '-translate-y-1/2',
                  'flex h-11 w-11 items-center justify-center',
                  'rounded-full bg-black/55',
                  'text-white backdrop-blur-md',
                  'transition hover:bg-white/15',
                  'sm:right-6'
                )}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function MobileCommunityPill({
  children,
  icon,
}: {
  children: React.ReactNode
  icon?: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'inline-flex h-8 items-center gap-1.5',
        'rounded-full border border-border',
        'bg-muted/30 px-3',
        'text-[11px] font-medium text-foreground/80'
      )}
    >
      {icon}

      {children}
    </div>
  )
}

function formatCompactCount(
  value: number
) {
  if (value >= 1_000_000) {
    return `${(
      value / 1_000_000
    ).toFixed(
      value >= 10_000_000
        ? 0
        : 1
    )}M`
  }

  if (value >= 1000) {
    return `${(
      value / 1000
    ).toFixed(
      value >= 10_000
        ? 0
        : 1
    )}k`
  }

  return String(value)
}

function MobileMemberProof({
  members,
  memberCount,
}: {
  members: PublicMember[]
  memberCount: number
}) {
  const visible =
    members.slice(0, 4)

  if (
    !visible.length &&
    memberCount <= 0
  ) {
    return null
  }

  const compactCount =
    formatCompactCount(
      memberCount
    )

  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="flex -space-x-2">
        {visible.map(
          (
            member,
            index
          ) => {
            const name =
              memberName(
                member
              )

            const src =
              resolvePublicAsset(
                member.avatar ||
                  member.image
              )

            return (
              <div
                key={String(
                  member.customer_id ??
                    member.id ??
                    index
                )}
                className={cn(
                  'h-8 w-8 overflow-hidden rounded-full',
                  'border-2 border-background',
                  'bg-muted'
                )}
              >
                {src ? (
                  <img
                    src={src}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[9px] font-semibold">
                    {getInitials(
                      name
                    )}
                  </div>
                )}
              </div>
            )
          }
        )}
      </div>

      {memberCount > 0 && (
        <span className="text-[11px] font-semibold text-foreground/80">
          {compactCount}
        </span>
      )}
    </div>
  )
}

function GalleryMediaPreview({
  item,
  mode,
}: {
  item: MediaItem
  mode:
    | 'mobile-main'
    | 'thumbnail'
    | 'desktop-carousel'
}) {
  if (
    item.type ===
    'video'
  ) {
    if (
      mode ===
      'thumbnail'
    ) {
      return (
        <div className="relative flex h-full w-full items-center justify-center bg-black">
          <Video className="h-5 w-5 text-white/80" />

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white">
              <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
            </div>
          </div>
        </div>
      )
    }

    /*
     * En el carrusel el video no se reproduce inline.
     * El click completo abre el lightbox.
     *
     * pointer-events-none evita que el iframe
     * intercepte el click.
     */
    return (
      <div
        className={cn(
          'pointer-events-none',
          'relative flex items-center justify-center bg-black',
          mode ===
            'desktop-carousel'
            ? 'h-[390px] w-[693px]'
            : 'aspect-video w-full'
        )}
      >
        <VideoEmbed
          url={
            item.url
          }
          rounded="none"
          variant="glass"
          className="h-full w-full"
        />

        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur">
            <Play className="ml-1 h-5 w-5 fill-current" />
          </div>
        </div>
      </div>
    )
  }

  if (
    mode ===
    'desktop-carousel'
  ) {
    return (
      <img
        src={
          item.src
        }
        alt=""
        draggable={
          false
        }
        className={cn(
          'block',
          'h-[390px] w-auto',
          'max-w-[700px]',
          'object-contain'
        )}
      />
    )
  }

  if (
    mode ===
    'mobile-main'
  ) {
    return (
      <img
        src={
          item.src
        }
        alt=""
        draggable={
          false
        }
        className={cn(
          'block',
          'max-h-[70dvh]',
          'max-w-full',
          'h-auto w-auto',
          'object-contain'
        )}
      />
    )
  }

  return (
    <img
      src={
        item.src
      }
      alt=""
      draggable={
        false
      }
      className="h-full w-full object-contain"
    />
  )
}

function HubeeAttribution() {
  const hubeeUrl =
    process.env.NEXT_PUBLIC_BASE_URL ||
    'https://hubee.app'

  return (
    <a
      href={hubeeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group inline-flex h-9 items-center gap-1.5',
        'rounded-xl px-2.5 sm:px-3',
        'text-[11px] font-medium',
        'text-muted-foreground',
        'transition',
        'hover:bg-muted/60',
        'hover:text-foreground'
      )}
    >
      <span className="sm:inline">
        Created with
      </span>

      <img
        src="/images/hubee-mark.svg"
        alt="Hubee"
        className="h-4 w-auto pl-1 dark:invert"
        style={{
          imageRendering: 'auto',
        }}
      />
    </a>
  )
}

/* -------------------------------------------------------------------------- */
/*                              HERO COMPONENTS                               */
/* -------------------------------------------------------------------------- */

function HeroPill({
  children,
  icon,
}: {
  children:
    React.ReactNode
  icon?:
    React.ReactNode
}) {
  return (
    <div className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-background/70 px-3 text-xs font-medium text-foreground/80 backdrop-blur">
      {icon}
      {children}
    </div>
  )
}

function PrimaryJoinButton({
  loggedIn,
  isMember,
  isPending,
  joinLabel,
  onClick,
  large = false,
}: {
  loggedIn: boolean
  isMember: boolean
  isPending: boolean
  joinLabel: string
  onClick: () => void
  large?: boolean
}) {
  let label =
    joinLabel

  if (
    loggedIn &&
    isMember
  ) {
    label =
      'Entrar a la comunidad'
  }

  if (
    loggedIn &&
    isPending
  ) {
    label =
      'Solicitud pendiente'
  }

  return (
    <button
      type="button"
      disabled={
        isPending
      }
      onClick={
        onClick
      }
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl',
        'bg-primary font-semibold text-primary-foreground',
        'shadow-lg shadow-primary/10',
        'transition hover:brightness-105 active:scale-[0.99]',
        'disabled:cursor-default disabled:opacity-60',

        large
          ? 'h-12 px-8 text-sm'
          : 'h-11 min-w-[210px] px-6 text-sm'
      )}
    >
      {label}

      {!isPending && (
        <ArrowRight className="h-4 w-4" />
      )}
    </button>
  )
}

function MiniMemberProof({
  members,
  memberCount,
}: {
  members:
    PublicMember[]
  memberCount: number
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex -space-x-2">
        {members
          .slice(0, 6)
          .map(
            (
              member,
              index
            ) => {
              const src =
                resolvePublicAsset(
                  member.avatar ||
                    member.image
                )

              const name =
                memberName(
                  member
                )

              return (
                <div
                  key={
                    String(
                      member.customer_id ??
                        member.id ??
                        index
                    )
                  }
                  className="h-8 w-8 overflow-hidden rounded-full border-2 border-background bg-muted"
                >
                  {src ? (
                    <img
                      src={
                        src
                      }
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold">
                      {getInitials(
                        name
                      )}
                    </div>
                  )}
                </div>
              )
            }
          )}
      </div>

      {memberCount >
        0 && (
        <div className="truncate text-xs text-muted-foreground">
          +
          {memberCount.toLocaleString(
            'es-MX'
          )}{' '}
          personas ya están dentro
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                 MEDIA                                     */
/* -------------------------------------------------------------------------- */

function MediaThumb({
  item,
  selected,
  onSelect,
  onRemove,
}: {
  item: MediaItem
  selected: boolean
  onSelect: () => void
  onRemove?: () => void
}) {
  return (
    <button
      type="button"
      onClick={
        onSelect
      }
      className={cn(
        'group relative overflow-hidden rounded-[18px] border bg-muted',
        'transition hover:brightness-105',
        selected
          ? 'border-primary'
          : 'border-border'
      )}
    >
      <div className="aspect-[4/3]">
        {item.type ===
        'image' ? (
          <img
            src={
              item.src
            }
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-black/80">
            <Play className="h-7 w-7 fill-white text-white" />
          </div>
        )}
      </div>

      {onRemove && (
        <button
          type="button"
          onClick={(
            event
          ) => {
            event.stopPropagation()
            onRemove()
          }}
          className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white opacity-0 backdrop-blur transition group-hover:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </button>
  )
}

function AddMediaTile({
  icon,
  label,
  onClick,
}: {
  icon:
    React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={
        onClick
      }
      className="flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-[18px] border border-dashed border-border bg-muted/30 text-muted-foreground transition hover:bg-muted hover:text-foreground"
    >
      {icon}
      <span className="text-xs font-medium">
        {label}
      </span>
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/*                              SECTION HEADING                               */
/* -------------------------------------------------------------------------- */

function SectionHeading({
  title,
  action,
}: {
  title: string
  action?: {
    label: string
    onClick: () => void
  }
}) {
  return (
    <div className="mb-2.5 flex items-center justify-between">
      <h2 className="text-base font-semibold tracking-tight sm:text-lg">
        {title}
      </h2>

      {action && (
        <button
          type="button"
          onClick={
            action.onClick
          }
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          <Plus className="h-3.5 w-3.5" />
          {
            action.label
          }
        </button>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                               HIGHLIGHTS                                   */
/* -------------------------------------------------------------------------- */

function HighlightCard({
  item,
  isOwner,
  onEdit,
  onDelete,
}: {
  item: HighlightItem
  isOwner: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const image =
    resolvePublicAsset(
      item.image
    )

  const content = (
    <div
      className={cn(
        'group relative h-full overflow-hidden rounded-[20px]',
        'border border-border bg-card shadow-sm',
        'transition duration-200 hover:-translate-y-0.5 hover:shadow-md'
      )}
    >
      <div className="flex h-full min-h-[128px]">
        {image && (
          <div className="w-[42%] shrink-0 overflow-hidden bg-muted">
            <img
              src={image}
              alt=""
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
            />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col p-4">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug">
            {
              item.title
            }
          </h3>

          {item.description && (
            <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-muted-foreground">
              {
                item.description
              }
            </p>
          )}

          <div className="mt-auto flex items-center gap-1 pt-3 text-xs font-semibold text-primary">
            {item.cta ||
              'Ver más'}
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>

      {isOwner && (
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition group-hover:opacity-100">
          <button
            type="button"
            onClick={(
              event
            ) => {
              event.preventDefault()
              event.stopPropagation()
              onEdit()
            }}
            className="rounded-full bg-background/90 p-2 shadow-sm backdrop-blur"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            onClick={(
              event
            ) => {
              event.preventDefault()
              event.stopPropagation()
              onDelete()
            }}
            className="rounded-full bg-background/90 p-2 text-destructive shadow-sm backdrop-blur"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  )

  if (
    item.url &&
    !isOwner
  ) {
    return (
      <a
        href={
          item.url
        }
        target={
          item.url.startsWith(
            'http'
          )
            ? '_blank'
            : undefined
        }
        rel={
          item.url.startsWith(
            'http'
          )
            ? 'noreferrer'
            : undefined
        }
        className="block h-full"
      >
        {content}
      </a>
    )
  }

  return content
}

/* -------------------------------------------------------------------------- */
/*                                  SPACES                                    */
/* -------------------------------------------------------------------------- */

function PublicSpaceCard({
  space,
  onJoin,
  isMember,
  fadeRight = false,
}: {
  space: PublicSpace
  onJoin: () => void
  isMember: boolean
  fadeRight?: boolean
}) {
  const title =
    space.name ||
    space.title ||
    'Espacio'

  const image =
    resolvePublicAsset(
      space.image ||
        space.image_url
    )

  const id =
    space.space_id ??
    space.category_id ??
    space.id

  return (
    <div className="group relative overflow-hidden rounded-[20px] border border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      {image ? (
        <div className="aspect-[16/8] overflow-hidden bg-muted">
          <img
            src={image}
            alt=""
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
          />
        </div>
      ) : (
        <div className="flex aspect-[16/8] items-center justify-center bg-muted/50">
          <div className="text-3xl">
            {space.icon ||
              '✦'}
          </div>
        </div>
      )}

      <div className="p-4">
        <h3 className="truncate text-sm font-semibold">
          {title}
        </h3>

        {space.description && (
          <p className="mt-1 line-clamp-2 min-h-[38px] text-xs leading-5 text-muted-foreground">
            {space.description}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Globe2 className="h-3 w-3" />
            Público
          </span>

          {isMember && id ? (
            <a
              href={`/spaces/${id}`}
              className="inline-flex items-center gap-1 text-xs font-medium"
            >
              Ver espacio
              <ChevronRight className="h-3.5 w-3.5" />
            </a>
          ) : (
            <button
              type="button"
              onClick={onJoin}
              className="inline-flex items-center gap-1 text-xs font-medium"
            >
              Ver espacio
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {fadeRight && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[42%] bg-gradient-to-r from-transparent via-background/45 to-background" />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                  MEMBERS                                   */
/* -------------------------------------------------------------------------- */

function MemberAvatarRow({
  members,
  memberCount,
}: {
  members:
    PublicMember[]
  memberCount: number
}) {
  const visible =
    members.slice(
      0,
      13
    )

  return (
    <div className="flex min-w-0 items-center">
      <div className="flex -space-x-1.5 overflow-hidden py-1">
        {visible.map(
          (
            member,
            index
          ) => {
            const name =
              memberName(
                member
              )

            const src =
              resolvePublicAsset(
                member.avatar ||
                  member.image
              )

            return (
              <div
                key={
                  String(
                    member.customer_id ??
                      member.id ??
                      index
                  )
                }
                title={name}
                className="h-9 w-9 shrink-0 overflow-hidden rounded-full border-2 border-background bg-muted shadow-sm"
              >
                {src ? (
                  <img
                    src={src}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold">
                    {getInitials(
                      name
                    )}
                  </div>
                )}
              </div>
            )
          }
        )}
      </div>

      {memberCount >
        visible.length && (
        <div className="ml-2 flex h-9 min-w-9 items-center justify-center rounded-full border border-border bg-muted px-2 text-xs font-semibold">
          +
          {memberCount -
            visible.length >
          99
            ? '99'
            : memberCount -
              visible.length}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                               EMPTY STATES                                 */
/* -------------------------------------------------------------------------- */

function EmptyEditorCard({
  title,
  description,
  onClick,
}: {
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={
        onClick
      }
      className="flex w-full items-center gap-4 rounded-[20px] border border-dashed border-border bg-muted/20 p-5 text-left transition hover:bg-muted/40"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-muted">
        <Plus className="h-5 w-5" />
      </div>

      <div>
        <div className="text-sm font-semibold">
          {title}
        </div>

        <div className="mt-1 max-w-2xl text-xs leading-5 text-muted-foreground">
          {description}
        </div>
      </div>
    </button>
  )
}

function EmptyPreviewCard({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div className="rounded-[20px] border border-dashed border-border bg-muted/20 p-5">
      <div className="text-sm font-semibold">
        {title}
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        {description}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                           PAGE VISIBILITY EDITOR                           */
/* -------------------------------------------------------------------------- */

function PageVisibilityEditor({
  modules,
  onSave,
}: {
  modules: AboutModule[]
  onSave: (value: {
    highlights: boolean
    spaces: boolean
    members: boolean
  }) => Promise<void>
}) {
  const enabled = (
    key: string,
    fallback = false
  ) => {
    const module =
      modules.find(
        item =>
          item.module_key ===
          key
      )

    return module
      ? module.enabled
      : fallback
  }

  const [
    highlights,
    setHighlights,
  ] = useState(
    enabled(
      'highlights'
    )
  )

  const [spaces, setSpaces] =
    useState(
      enabled('spaces')
    )

  const [
    members,
    setMembers,
  ] = useState(
    enabled('members')
  )

  const [
    saving,
    setSaving,
  ] = useState(false)

  return (
    <div className="p-5">
      <div>
        <h2 className="text-lg font-semibold">
          Página pública
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          Elige qué secciones quieres mostrar a personas que todavía no forman parte de tu comunidad.
        </p>
      </div>

      <div className="mt-5 space-y-2">
        <VisibilityRow
          title="Destacados"
          description="Recursos, video de bienvenida, redes, WhatsApp y links externos."
          enabled={
            highlights
          }
          onToggle={() =>
            setHighlights(
              !highlights
            )
          }
        />

        <VisibilityRow
          title="Espacios"
          description="Muestra una vista previa de las áreas principales de tu comunidad."
          enabled={
            spaces
          }
          onToggle={() =>
            setSpaces(
              !spaces
            )
          }
        />

        <VisibilityRow
          title="Miembros"
          description="Prueba social con una selección de miembros de la comunidad."
          enabled={
            members
          }
          onToggle={() =>
            setMembers(
              !members
            )
          }
        />
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          disabled={
            saving
          }
          onClick={async () => {
            setSaving(
              true
            )

            try {
              await onSave({
                highlights,
                spaces,
                members,
              })
            } finally {
              setSaving(
                false
              )
            }
          }}
          className="rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background disabled:opacity-60"
        >
          {saving
            ? 'Guardando…'
            : 'Guardar'}
        </button>
      </div>
    </div>
  )
}

function VisibilityRow({
  title,
  description,
  enabled,
  onToggle,
}: {
  title: string
  description: string
  enabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={
        onToggle
      }
      className="flex w-full items-center gap-4 rounded-2xl border border-border p-4 text-left transition hover:bg-muted/30"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">
          {title}
        </div>

        <div className="mt-1 text-xs leading-5 text-muted-foreground">
          {description}
        </div>
      </div>

      <div
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition',
          enabled
            ? 'bg-primary'
            : 'bg-muted'
        )}
      >
        <div
          className={cn(
            'absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition',
            enabled
              ? 'left-6'
              : 'left-1'
          )}
        />
      </div>
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/*                             HIGHLIGHT EDITOR                               */
/* -------------------------------------------------------------------------- */

function HighlightEditor({
  sellerId,
  initial,
  openImageManager,
  onCancel,
  onSave,
}: {
  sellerId: number
  initial:
    | HighlightItem
    | null
  openImageManager: (
    options: any
  ) => void
  onCancel: () => void
  onSave: (
    item: HighlightItem
  ) => Promise<void>
}) {
  const [
    title,
    setTitle,
  ] = useState(
    initial?.title ||
      ''
  )

  const [
    description,
    setDescription,
  ] = useState(
    initial?.description ||
      ''
  )

  const [
    image,
    setImage,
  ] = useState(
    initial?.image ||
      ''
  )

  const [
    url,
    setUrl,
  ] = useState(
    initial?.url || ''
  )

  const [
    cta,
    setCta,
  ] = useState(
    initial?.cta ||
      'Ver ahora'
  )

  const [
    saving,
    setSaving,
  ] = useState(false)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">
          {initial
            ? 'Editar destacado'
            : 'Nuevo destacado'}
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          Usa esta tarjeta para mostrar algo que ayude a una persona a entender el valor de tu comunidad.
        </p>
      </div>

      <div>
        <div className="text-xs font-medium">
          Imagen
        </div>

        <button
          type="button"
          onClick={() => {
            openImageManager({
              sellerId,
              onSelect: (
                img: any
              ) => {
                const selected =
                  String(
                    img?.url ||
                      ''
                  )

                if (
                  selected
                ) {
                  setImage(
                    selected
                  )
                }
              },
            })
          }}
          className="mt-2 flex aspect-[16/6] w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-border bg-muted/20"
        >
          {image ? (
            <img
              src={resolvePublicAsset(
                image
              )}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ImagePlus className="h-4 w-4" />
              Seleccionar imagen
            </div>
          )}
        </button>
      </div>

      <EditorField
        label="Título"
        value={title}
        onChange={
          setTitle
        }
        placeholder="Ej. Recursos gratuitos"
      />

      <EditorTextArea
        label="Descripción"
        value={
          description
        }
        onChange={
          setDescription
        }
        placeholder="Explica brevemente qué encontrará aquí."
      />

      <EditorField
        label="Link"
        value={url}
        onChange={
          setUrl
        }
        placeholder="https://..."
      />

      <EditorField
        label="Texto del botón"
        value={cta}
        onChange={
          setCta
        }
        placeholder="Ver ahora"
      />

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={
            onCancel
          }
          className="rounded-xl border border-border px-4 py-2 text-sm"
        >
          Cancelar
        </button>

        <button
          type="button"
          disabled={
            saving ||
            !title.trim()
          }
          onClick={async () => {
            setSaving(
              true
            )

            try {
              await onSave({
                id:
                  initial?.id ||
                  `highlight_${Date.now()}`,
                title:
                  title.trim(),
                description:
                  description.trim(),
                image:
                  image ||
                  undefined,
                url:
                  url.trim() ||
                  undefined,
                cta:
                  cta.trim() ||
                  'Ver ahora',
                enabled:
                  true,
              })
            } finally {
              setSaving(
                false
              )
            }
          }}
          className="rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background disabled:opacity-50"
        >
          {saving
            ? 'Guardando…'
            : 'Guardar'}
        </button>
      </div>
    </div>
  )
}

function EditorField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (
    value: string
  ) => void
  placeholder?: string
}) {
  return (
    <label className="block">
      <div className="mb-2 text-xs font-medium">
        {label}
      </div>

      <input
        value={value}
        onChange={(
          event
        ) =>
          onChange(
            event.target.value
          )
        }
        placeholder={
          placeholder
        }
        className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  )
}

function EditorTextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (
    value: string
  ) => void
  placeholder?: string
}) {
  return (
    <label className="block">
      <div className="mb-2 text-xs font-medium">
        {label}
      </div>

      <textarea
        value={value}
        onChange={(
          event
        ) =>
          onChange(
            event.target.value
          )
        }
        placeholder={
          placeholder
        }
        rows={3}
        className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  )
}